// src/lib/omnicine/snapTime.ts

/**
 * snapTime
 *
 * Canonical time snapping utility for OmniCine.
 * Pure function. No side effects. Seconds-only.
 *
 * Supported snap modes:
 * - frame   → snap to frame boundary
 * - second  → snap to whole seconds
 * - beat    → snap to beat grid (60 / fps)
 * - marker  → snap to nearest marker
 */

export type SnapMode = "frame" | "second" | "beat" | "marker";

export interface SnapConfig {
  enabled: boolean;
  mode: SnapMode;
  fps: number;
  bpm?: number;
  beatDiv?: number;
  markers?: { id: string; timeSec: number }[];
}

export function snapTime(
  timeSec: number,
  config: SnapConfig
): number {
  if (!config.enabled) return timeSec;

  switch (config.mode) {
    case "frame": {
      const frame = 1 / Math.max(1, config.fps || 30);
      return Math.round(timeSec / frame) * frame;
    }

    case "second": {
      return Math.round(timeSec);
    }

    case "beat": {
      const bpm = Math.max(1, config.bpm || 120);
      const beatDiv = Math.max(1, Math.floor(config.beatDiv || 1));
      const beat = 60 / bpm / beatDiv;
      return Math.round(timeSec / beat) * beat;
    }

    case "marker": {
      const markers = config.markers ?? [];
      if (!markers.length) return timeSec;

      let closest = markers[0].timeSec;
      let bestDist = Math.abs(timeSec - closest);

      for (const m of markers) {
        const d = Math.abs(timeSec - m.timeSec);
        if (d < bestDist) {
          bestDist = d;
          closest = m.timeSec;
        }
      }

      return closest;
    }

    default:
      return timeSec;
  }
}
